import { useAuthStore } from '@/stores/auth.store';
import {
  getSharedRefreshToken,
  setSharedAuthToken,
  decodeToken,
} from '@/lib/shared-auth';

// Hub API - for projects, tasks, messages, etc.
const API_BASE = '/api/v1';

// Accounts API - for organizations, apps, users
// VITE_ACCOUNTS_API_URL should point to the Accounts API base (e.g. https://accounts.soliddigital.com/api/v1)
// If not provided, fall back to the production URL.

const ACCOUNTS_API_BASE =
  import.meta.env.VITE_ACCOUNTS_API_URL || 'https://accounts.soliddigital.com/api/v1';

// solid-hub application ID in accounts database (used to fetch org assignments)
const SOLID_HUB_APP_ID = 'cmklml66p000110twkhycj8ja';

// Token refresh state - prevents concurrent refresh attempts
let refreshPromise: Promise<string | null> | null = null;

/**
 * Get the access token from OIDC storage.
 * The app-rail stores tokens here after authenticating via the Auth service.
 */
function getAccessToken(): string | null {
  try {
    // Get token from auth store (backed by localStorage 'solid-hub-auth')
    const token = useAuthStore.getState().accessToken;
    if (token) {
      return token;
    }
  } catch (e) {
    console.error('[API] Failed to get access token:', e);
  }
  return null;
}

/**
 * Get the refresh token from storage
 */
function getRefreshToken(): string | null {
  // Try localStorage first (stored during auth callback)
  try {
    const stored = localStorage.getItem('solid-hub-auth');
    if (stored) {
      const data = JSON.parse(stored);
      if (data.state?.refreshToken) {
        return data.state.refreshToken;
      }
    }
  } catch {
    // Ignore parse errors
  }

  // Fall back to shared cookie
  return getSharedRefreshToken();
}

/**
 * Refresh the access token using the refresh token.
 * Returns the new access token or null if refresh failed.
 * Only one refresh can happen at a time - concurrent calls wait for the first.
 */
async function refreshAccessToken(): Promise<string | null> {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log('[API] No refresh token available');
    return null;
  }

  refreshPromise = (async () => {
    try {
      console.log('[API] Attempting token refresh...');

      const response = await fetch('/oauth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: 'solid-hub',
          refresh_token: refreshToken,
        }).toString(),
      });

      if (!response.ok) {
        console.log('[API] Token refresh failed with status:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.error || !data.access_token) {
        console.log('[API] Token refresh error:', data.error || 'No access token in response');
        return null;
      }

      const { access_token: newAccessToken, refresh_token: newRefreshToken } = data;

      // Decode token for user info
      let user = undefined;
      const payload = decodeToken(newAccessToken);
      if (payload) {
        user = {
          id: payload.sub as string,
          email: payload.email as string,
          name: payload.name as string,
          avatarUrl: payload.picture as string | undefined,
          organizations: (payload.orgs as any[]) || [],
        };
      }

      // Update auth store with new token
      useAuthStore.getState().setAuth(newAccessToken, user);

      // Update shared cookie for cross-subdomain SSO
      setSharedAuthToken(newAccessToken, newRefreshToken || refreshToken);

      // Update refresh token in localStorage if we got a new one
      if (newRefreshToken) {
        try {
          const stored = localStorage.getItem('solid-hub-auth');
          if (stored) {
            const storageData = JSON.parse(stored);
            storageData.state.refreshToken = newRefreshToken;
            localStorage.setItem('solid-hub-auth', JSON.stringify(storageData));
          }
        } catch {
          // Ignore storage errors
        }
      }

      console.log('[API] Token refresh successful');
      return newAccessToken;
    } catch (error) {
      console.error('[API] Token refresh error:', error);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

class ApiClient {
  constructor(private baseUrl: string = API_BASE) {}

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Execute a request with automatic token refresh on 401.
   * If we get a 401, attempt to refresh the token and retry once.
   */
  private async request<T>(
    method: string,
    endpoint: string,
    body?: string,
  ): Promise<T> {
    const doFetch = () =>
      fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: this.getHeaders(),
        body,
      });

    let response = await doFetch();

    // On 401, try to refresh token and retry once
    if (response.status === 401) {
      const hasToken = useAuthStore.getState().accessToken;
      if (hasToken) {
        console.log('[API] Got 401, attempting token refresh...');
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the request with the new token
          console.log('[API] Token refreshed, retrying request...');
          response = await doFetch();

          // If still 401 after refresh, give up
          if (response.status === 401) {
            console.log('[API] Still 401 after refresh, logging out');
            useAuthStore.getState().logout({ skipOidcLogout: true });
            throw new Error('Session expired');
          }
        } else {
          // Refresh failed, logout
          console.log('[API] Token refresh failed, logging out');
          useAuthStore.getState().logout({ skipOidcLogout: true });
          throw new Error('Session expired');
        }
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      const err = new Error(Array.isArray(error.message) ? error.message.join(', ') : error.message || 'Request failed');
      // Keep the server's body (e.g. per-field `errors` on form submission) for callers that want it.
      (err as any).status = response.status;
      (err as any).body = error;
      throw err;
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>('GET', endpoint);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>('POST', endpoint, data ? JSON.stringify(data) : undefined);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>('PATCH', endpoint, JSON.stringify(data));
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>('PUT', endpoint, JSON.stringify(data));
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>('DELETE', endpoint);
  }
}

// Hub API client - projects, tasks, messages, etc.
export const api = new ApiClient(API_BASE);

// Accounts API client - organizations, apps, users
export const shellApi = new ApiClient(ACCOUNTS_API_BASE);

// Auth API (legacy - kept for backwards compatibility, not used in current Hub flow)
export const authApi = {
  // In the new architecture, auth is handled via OIDC + Supabase and Accounts.
  // These endpoints are no-ops unless a compatible backend is provided.
  getConfig: () => shellApi.get<{ provider: string; isDevMode: boolean }>('/auth/config'),
  getDevUsers: () => shellApi.get<Array<{
    id: string;
    email: string;
    name: string;
    role: string;
    organizationId: string;
    organizationName: string;
    avatarUrl?: string;
  }>>('/auth/dev/users'),
  devLogin: (userId: string) =>
    shellApi.post<{ accessToken: string; expiresIn: number }>('/auth/dev/login', { userId }),
};

// Organizations API - uses Hub API's own organizations endpoint
// which returns client orgs from OIDC claims enriched with project counts
export const organizationsApi = {
  list: async (_params?: { type?: string; all?: boolean }) => {
    const result = await api.get<{
      organizations: Array<{
        id: string;
        name: string;
        slug: string;
        logo: string | null;
        /** Accounts org kind (SUPER | AGENCY | CLIENT) and parent, when known */
        kind?: string | null;
        parentOrgId?: string | null;
        _count: { members: number; projects: number };
        latestProjectActivity: string | null;
        isFavorited: boolean;
      }>;
      total: number;
    }>('/organizations');

    return result.organizations;
  },
  get: (id: string) => api.get<any>(`/organizations/${id}`),
  favoriteOrg: (id: string) => api.post<{ success: boolean }>(`/organizations/${id}/favorite`, {}),
  unfavoriteOrg: (id: string) => api.delete<{ success: boolean }>(`/organizations/${id}/favorite`),
  create: (data: any) => shellApi.post<any>('/organizations', data),
  update: (id: string, data: any) => shellApi.patch<any>(`/organizations/${id}`, data),
  getMembers: async (id: string) => {
    // Hub API: GET /organizations/:id/members -> { members, total }
    const result = await api.get<{
      members: Array<{
        id: string;
        userId: string;
        role: string;
        createdAt: string;
        user: { id: string; email: string; name: string; picture?: string | null };
      }>;
      total: number;
    }>(`/organizations/${id}/members`);

    return result.members.map((m) => ({
      id: m.id,
      userId: m.userId,
      role: m.role,
      roleId: m.role,
      isOwner: m.role === 'org_owner',
      createdAt: m.createdAt,
      user: {
        id: m.user.id,
        email: m.user.email,
        name: m.user.name,
        avatarUrl: m.user.picture ?? undefined,
      },
    }));
  },
  // Static org roles for Accounts
  getRoles: async () =>
    Promise.resolve([
      { id: 'org_owner', name: 'Owner' },
      { id: 'org_admin', name: 'Admin' },
      { id: 'org_member', name: 'Member' },
    ]),
  updateMemberRole: (orgId: string, userId: string, role: string) =>
    shellApi.patch<any>(`/organizations/${orgId}/members/${userId}`, { role }),
  syncMembers: (orgId: string) =>
    api.post<{ synced: number; created: number; existing: number; message: string }>(
      `/organizations/${orgId}/sync-members`
    ),
};

// Mentionable user type
export interface MentionableUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

// Project link type
export interface ProjectLink {
  id: string;
  projectId: string;
  name: string;
  url: string;
  position: number;
  createdAt: string;
  updatedAt: string;
}

// Projects API
export const projectsApi = {
  list: (params?: { organizationId?: string; archived?: boolean; internalOnly?: boolean; all?: boolean; debug?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.organizationId) query.append('organizationId', params.organizationId);
    if (params?.archived) query.append('archived', 'true');
    if (params?.internalOnly) query.append('internalOnly', 'true');
    if (params?.all) query.append('all', 'true');
    if (params?.debug) query.append('debug', 'true');
    return api.get<any>(`/projects?${query.toString()}`);
  },
  get: (id: string) => api.get<any>(`/projects/${id}`),
  create: (data: { name: string; description?: string; organizationId: string; clientOrganizationId?: string; isPrivate?: boolean }) =>
    api.post<any>('/projects', data),
  update: (id: string, data: { name?: string; description?: string; isPrivate?: boolean; settings?: Record<string, any> }) =>
    api.patch<any>(`/projects/${id}`, data),
  archive: (id: string) => api.post<any>(`/projects/${id}/archive`, {}),
  unarchive: (id: string) => api.post<any>(`/projects/${id}/unarchive`, {}),
  delete: (id: string) => api.delete<any>(`/projects/${id}`),
  join: (id: string) => api.post<any>(`/projects/${id}/join`, {}),
  getMentionableUsers: (id: string) => api.get<MentionableUser[]>(`/projects/${id}/mentionable-users`),
  // Member management
  addMember: (id: string, userId: string, userInfo?: { email?: string; name?: string; avatarUrl?: string }) =>
    api.post<any>(`/projects/${id}/members`, { userId, ...userInfo }),
  removeMember: (id: string, userId: string) => api.delete<any>(`/projects/${id}/members/${userId}`),
  searchUsers: (id: string, query: string) =>
    api.get<Array<{
      userId: string;
      user: { id: string; name: string; email: string; avatarUrl?: string };
      isMember: boolean;
    }>>(`/projects/${id}/search-users?q=${encodeURIComponent(query)}`),
  // Notification defaults
  getNotificationDefaults: (id: string) => api.get<string[]>(`/projects/${id}/notification-defaults`),
  setNotificationDefaults: (id: string, userIds: string[]) =>
    api.put<{ success: boolean }>(`/projects/${id}/notification-defaults`, { userIds }),
  // Project links
  getLinks: (id: string) => api.get<ProjectLink[]>(`/projects/${id}/links`),
  createLink: (id: string, data: { name: string; url: string }) =>
    api.post<ProjectLink>(`/projects/${id}/links`, data),
  updateLink: (linkId: string, data: { name?: string; url?: string }) =>
    api.patch<ProjectLink>(`/projects/links/${linkId}`, data),
  deleteLink: (linkId: string) => api.delete<void>(`/projects/links/${linkId}`),
  reorderLinks: (projectId: string, linkIds: string[]) =>
    api.patch<void>(`/projects/${projectId}/links/reorder`, { linkIds }),
  favorite: (id: string) => api.post<{ success: boolean }>(`/projects/${id}/favorite`, {}),
  unfavorite: (id: string) => api.delete<{ success: boolean }>(`/projects/${id}/favorite`),
  // "At a glance" figures for the project overview
  stats: (id: string) => api.get<ProjectStats>(`/projects/${id}/stats`),
};

/** GET /projects/:id/stats — see docs/project-overview.md. */
export interface ProjectStats {
  /** Which side the viewer is on; decides what "waiting" lists. */
  perspective: 'team' | 'client';
  waiting: {
    total: number;
    discussions: Array<{ id: string; title: string | null; discussionType: string | null }>;
  };
  /** Median team reply time, weekends excluded. Null below 3 answered messages. */
  responseTime: { medianMs: number; samples: number } | null;
  /** Posts + replies per week, oldest first; weekStart is a Monday, YYYY-MM-DD. */
  activity: { weeks: Array<{ weekStart: string; count: number }> };
  windowDays: number;
  timeZone: string;
}

// Discussion status type
export type DiscussionStatusType = 'open' | 'in_progress' | 'waiting_on_solid' | 'waiting_on_client' | 'on_hold' | 'closed';

// Who has viewed a discussion (agency-admin-only payload; absent otherwise)
export interface DiscussionSeenBy {
  userId: string;
  name: string | null;
  avatarUrl?: string | null;
  lastReadAt: string;
}

// Discussions API (Messages directly on projects - no boards)
export const discussionsApi = {
  list: (projectId: string, page = 1, archived = false, discussionType?: string, scheduled = false) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('archived', String(archived));
    if (discussionType) params.set('type', discussionType);
    if (scheduled) params.set('scheduled', 'true');
    return api.get<{ data: Array<any>; pagination: any }>(`/projects/${projectId}/discussions?${params.toString()}`);
  },
  get: (id: string) => api.get<any>(`/messages/${id}`),
  // Project admins / agency: turn a task into a discussion. `move` removes the task, `copy` keeps and links it.
  convertTask: (taskId: string, data: { mode: 'move' | 'copy'; discussionType?: string }) =>
    api.post<any>(`/messages/from-task/${taskId}`, data),
  create: (projectId: string, data: { title: string; content: string; notifyUserIds?: string[]; discussionType?: string; scheduledFor?: string; attachmentIds?: string[] }) =>
    api.post<any>(`/projects/${projectId}/discussions`, data),
  update: (id: string, data: any) => api.patch<any>(`/messages/${id}`, data),
  delete: (id: string) => api.delete<any>(`/messages/${id}`),
  createReply: (messageId: string, data: { content: string; notifyUserIds?: string[]; attachmentIds?: string[] }) =>
    api.post<any>(`/messages/${messageId}/replies`, data),
  archive: (id: string) => api.post<any>(`/messages/${id}/archive`, {}),
  unarchive: (id: string) => api.post<any>(`/messages/${id}/unarchive`, {}),
  // Opt in/out of discussion notifications
  updateSelfParticipation: (id: string, optedOut: boolean) =>
    api.patch<any>(`/messages/${id}/participants/me`, { optedOut }),
  // Agency: take another participant off the thread, or put them back
  updateParticipation: (id: string, userId: string, optedOut: boolean) =>
    api.patch<any>(`/messages/${id}/participants/${userId}`, { optedOut }),
  // Status management (agency-only)
  updateStatus: (id: string, status: DiscussionStatusType) =>
    api.patch<any>(`/messages/${id}/status`, { status }),
  // Important: highlighted and pinned to the top of the list (project admins)
  setImportant: (id: string, important: boolean) =>
    api.patch<{ id: string; importantAt: string | null }>(`/messages/${id}/important`, { important }),
  // AI summary backfill (agency admin only)
  backfillSummaries: () =>
    api.post<{ success: boolean; message: string }>('/messages/backfill-summaries'),
  // Read receipt: record that the current user viewed this discussion
  markRead: (id: string) => api.post<{ success: boolean; readAt: string }>(`/messages/${id}/read`, {}),
  // Create-task-from-discussion (agency admin only)
  ticketDraft: (id: string) =>
    api.post<{ title: string; description: string; aiGenerated: boolean }>(`/messages/${id}/ticket-draft`, {}),
  createTask: (id: string, data: { listId: string; title: string; description?: string; priority?: string }) =>
    api.post<any>(`/messages/${id}/tasks`, data),
  // Scheduled discussions: reschedule (future ISO date) or post now (null)
  updateSchedule: (id: string, scheduledFor: string | null) =>
    api.patch<any>(`/messages/${id}/schedule`, { scheduledFor }),
};

// Legacy alias for backwards compatibility during migration
export const messagesApi = discussionsApi;

// Tasks API
export const tasksApi = {
  listTaskLists: (projectId: string, includeArchived = false) =>
    api.get<Array<any>>(`/projects/${projectId}/task-lists${includeArchived ? '?includeArchived=true' : ''}`),
  getTaskList: (projectId: string, listId: string) => api.get<any>(`/projects/${projectId}/task-lists/${listId}`),
  createTaskList: (projectId: string, data: any) => api.post<any>(`/projects/${projectId}/task-lists`, data),
  updateTaskList: (projectId: string, listId: string, data: { name?: string; description?: string; visibility?: string }) =>
    api.patch<any>(`/projects/${projectId}/task-lists/${listId}`, data),
  checkVisibilityConflicts: (projectId: string, listId: string, visibility: string) =>
    api.get<Array<any>>(`/projects/${projectId}/task-lists/${listId}/conflicts?visibility=${visibility}`),
  deleteTaskList: (projectId: string, listId: string) =>
    api.delete<any>(`/projects/${projectId}/task-lists/${listId}`),
  archiveTaskList: (projectId: string, listId: string) =>
    api.patch<any>(`/projects/${projectId}/task-lists/${listId}/archive`, {}),
  unarchiveTaskList: (projectId: string, listId: string) =>
    api.patch<any>(`/projects/${projectId}/task-lists/${listId}/unarchive`, {}),
  reorderTaskLists: (projectId: string, listIds: string[]) =>
    api.patch<any>(`/projects/${projectId}/task-lists/reorder`, { listIds }),
  listTasks: (projectId: string, listId: string) =>
    api.get<Array<any>>(`/projects/${projectId}/task-lists/${listId}/tasks`),
  createTask: (projectId: string, listId: string, data: any) =>
    api.post<any>(`/projects/${projectId}/task-lists/${listId}/tasks`, data),
  getTask: (id: string) => api.get<any>(`/tasks/${id}`),
  updateTask: (id: string, data: any) => api.patch<any>(`/tasks/${id}`, data),
  reorderTask: (id: string, data: { statusId?: string; position: number }) =>
    api.patch<any>(`/tasks/${id}/reorder`, data),
  deleteTask: (id: string) => api.delete<any>(`/tasks/${id}`),
  toggleComplete: (id: string) => api.patch<any>(`/tasks/${id}/toggle`, {}),
  moveTask: (id: string, listId: string, position?: number) =>
    api.patch<any>(`/tasks/${id}/move`, { listId, position }),
  bulkUpdateTasks: (
    taskIds: string[],
    data: {
      statusId?: string;
      assigneeId?: string | null;
      dueDate?: string | null;
      priority?: string;
      listId?: string;
    },
  ) => api.patch<{ updated: number; skipped: number }>(`/tasks/bulk`, { taskIds, data }),
  bulkDeleteTasks: (taskIds: string[]) =>
    api.post<{ deleted: number; skipped: number }>(`/tasks/bulk/delete`, { taskIds }),
  getComments: (taskId: string) => api.get<Array<{
    id: string;
    content: string;
    createdAt: string;
    updatedAt: string;
    author: { id: string; name: string; avatarUrl?: string };
    attachments: Attachment[];
  }>>(`/tasks/${taskId}/comments`),
  addComment: (taskId: string, content: string) =>
    api.post<any>(`/tasks/${taskId}/comments`, { content }),
  updateComment: (taskId: string, commentId: string, content: string) =>
    api.patch<any>(`/tasks/${taskId}/comments/${commentId}`, { content }),
  getByDateRange: (projectId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    return api.get<CalendarTask[]>(`/projects/${projectId}/task-lists/tasks/by-date${query ? `?${query}` : ''}`);
  },
  getAllTasks: (options?: { status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString();
    return api.get<Array<{
      id: string;
      title: string;
      description?: string;
      status: string;
      priority: string;
      dueDate?: string;
      startDate?: string;
      assigneeId?: string;
      assignee?: { id: string; name: string; avatarUrl?: string; accountStatus?: string };
      createdBy?: { id: string; name: string; avatarUrl?: string };
      list: {
        id: string;
        name: string;
        project: { id: string; name: string; organizationId: string };
      };
      _count: { comments: number };
    }>>(`/tasks/all${query ? `?${query}` : ''}`);
  },
  duplicateTask: (id: string, data: {
    title: string;
    listId: string;
    copyEverything?: boolean;
    copyAttachments?: boolean;
    copyDueDate?: boolean;
    copyStartDate?: boolean;
    copyAssignee?: boolean;
    copyComments?: boolean;
    copyCommentAttachments?: boolean;
    keepStatus?: boolean;
    sendNotifications?: boolean;
  }) => api.post<any>(`/tasks/${id}/duplicate`, data),
  getMyTasks: (options?: { status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString();
    return api.get<Array<{
      id: string;
      title: string;
      description?: string;
      status: string;
      priority: string;
      dueDate?: string;
      startDate?: string;
      assignee: { id: string; name: string; avatarUrl?: string };
      list: {
        id: string;
        name: string;
        project: { id: string; name: string };
      };
      _count: { comments: number };
    }>>(`/tasks/my${query ? `?${query}` : ''}`);
  },
};

// Task Status Config API
export const taskStatusConfigsApi = {
  list: () => api.get<Array<{
    id: string;
    name: string;
    slug: string;
    color: string;
    position: number;
    isDefault: boolean;
    isClosed: boolean;
  }>>('/task-status-configs'),
  create: (data: { name: string; color: string; isDefault?: boolean; isClosed?: boolean }) =>
    api.post<any>('/task-status-configs', data),
  update: (id: string, data: { name?: string; color?: string; isDefault?: boolean; isClosed?: boolean }) =>
    api.patch<any>(`/task-status-configs/${id}`, data),
  reorder: (ids: string[]) =>
    api.patch<any>('/task-status-configs/reorder/positions', { ids }),
  remove: (id: string, reassignToId: string) =>
    api.delete<any>(`/task-status-configs/${id}?reassignToId=${reassignToId}`),
};

// ============================================
// API Keys — service keys for the /internal/* API
// ============================================

export type ApiKeyStatus = 'active' | 'expired' | 'revoked';

/**
 * An API key as the server returns it. There is no `keyHash` and no `key`:
 * the raw key exists on exactly ONE response in the whole app (the create call
 * below) and can never be produced again.
 */
export interface HubApiKey {
  id: string;
  name: string;
  /** The displayable head of the key — how you tell keys apart. */
  prefix: string;
  scopes: string[];
  status: ApiKeyStatus;
  createdById: string | null;
  createdByName: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string | null;
}

/** The create response — the only one carrying the raw key. Show it once. */
export interface CreatedHubApiKey extends HubApiKey {
  key: string;
}

export interface ApiKeyScope {
  value: string;
  label: string;
  description: string;
  resource: string;
}

export const apiKeysApi = {
  list: () => api.get<HubApiKey[]>('/api-keys'),
  scopes: () => api.get<ApiKeyScope[]>('/api-keys/scopes'),
  create: (data: { name: string; scopes: string[]; expiresAt?: string | null }) =>
    api.post<CreatedHubApiKey>('/api-keys', data),
  revoke: (id: string) => api.post<HubApiKey>(`/api-keys/${id}/revoke`),
};

// Files API (Google Drive integration)
export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  createdTime?: string;
  modifiedTime?: string;
  webViewLink?: string;
  thumbnailLink?: string;
  iconLink?: string;
  source: 'team' | 'client';
  isFolder: boolean;
}

export interface DriveFolder {
  id: string;
  name: string;
  mimeType: string;
}

export interface DriveConfig {
  projectId: string;
  teamFolderId?: string;
  teamFolderName?: string;
  clientUploadsFolderId?: string;
  clientUploadsFolderName?: string;
}

export const filesApi = {
  // Get drive integration status
  getStatus: (projectId: string) => api.get<{
    driveAvailable: boolean;
    configured: boolean;
    teamFolderConfigured: boolean;
    clientUploadsFolderConfigured: boolean;
    teamFolderName?: string;
    clientUploadsFolderName?: string;
    clientUploadsFolderId?: string;
  }>(`/projects/${projectId}/files/status`),

  // Get drive config (admin only)
  getConfig: (projectId: string) => api.get<DriveConfig>(`/projects/${projectId}/files/config`),

  // Update drive config (admin only)
  updateConfig: (projectId: string, data: { teamFolderId?: string; clientUploadsFolderId?: string }) =>
    api.patch<DriveConfig>(`/projects/${projectId}/files/config`, data),

  // List folders for picker (admin only)
  listFolders: (projectId: string, folderId: string = 'root') =>
    api.get<{ folders: DriveFolder[]; currentFolder?: DriveFolder; breadcrumb?: DriveFolder[] }>(
      `/projects/${projectId}/files/folders?folderId=${folderId}`
    ),

  // List files in project
  listFiles: (projectId: string, folderId?: string, source?: 'team' | 'client') => {
    const params = new URLSearchParams();
    if (folderId) params.append('folderId', folderId);
    if (source) params.append('source', source);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get<{ files: DriveFile[]; currentFolder?: DriveFolder; breadcrumb?: DriveFolder[]; driveAvailable: boolean }>(
      `/projects/${projectId}/files${query}`
    );
  },

  // Get file details
  getFile: (projectId: string, fileId: string) => api.get<DriveFile>(`/projects/${projectId}/files/${fileId}`),

  // Download file - returns URL to redirect to
  getDownloadUrl: (projectId: string, fileId: string) => `/api/v1/projects/${projectId}/files/${fileId}/download`,

  // Upload file
  uploadFile: async (projectId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('solid-hub-auth');
    const authData = token ? JSON.parse(token) : null;

    const response = await fetch(`/api/v1/projects/${projectId}/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authData?.state?.accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json() as Promise<DriveFile>;
  },
};

export interface IdentityConflict {
  otherUserId: string;
  email: string;
  name: string | null;
  lastSeenAt: string;
  projects: number;
  tasks: number;
  comments: number;
  discussions: number;
}

// Two Hub profiles, one email — see the API's IdentityConflictService.
export const identityApi = {
  getConflict: () => api.get<{ conflict: IdentityConflict | null }>('/auth/identity/conflict'),
  resolveConflict: (choice: 'claim' | 'separate') =>
    api.post<{ moved?: string; parked?: string }>('/auth/identity/conflict/resolve', { choice }),
};

// Notifications API
export const notificationsApi = {
  list: (page = 1) => api.get<{ data: Array<any>; pagination: any }>(`/notifications?page=${page}`),
  getUnreadCount: () => api.get<{ count: number }>('/notifications/unread-count'),
  markAsRead: (id: string) => api.patch<any>(`/notifications/${id}/read`, {}),
  markAllAsRead: () => api.patch<any>('/notifications/read-all', {}),
};

// Search API
export type SearchScope = 'project' | 'organization' | 'global';
export type SearchEntity = 'projects' | 'messages' | 'tasks' | 'comments' | 'events' | 'attachments';

export interface SearchResultItem {
  id: string;
  type: string;
  title: string;
  snippet: string;
  projectId: string | null;
  projectName: string | null;
  organizationId: string;
  organizationName: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  discussionType?: string | null;
}

export interface SearchResponse {
  query: string;
  scope: SearchScope;
  results: {
    projects: SearchResultItem[];
    messages: SearchResultItem[];
    tasks: SearchResultItem[];
    comments: SearchResultItem[];
    events: SearchResultItem[];
    attachments: SearchResultItem[];
  };
  totalCount: number;
  hasMore: boolean;
}

export interface SearchParams {
  q: string;
  scope: SearchScope;
  projectId?: string;
  organizationId?: string;
  entities?: SearchEntity[];
  limit?: number;
}

export const searchApi = {
  search: (params: SearchParams) => {
    const queryParams = new URLSearchParams();
    queryParams.set('q', params.q);
    queryParams.set('scope', params.scope);
    if (params.projectId) queryParams.set('projectId', params.projectId);
    if (params.organizationId) queryParams.set('organizationId', params.organizationId);
    if (params.entities) params.entities.forEach(e => queryParams.append('entities', e));
    if (params.limit) queryParams.set('limit', params.limit.toString());
    return api.get<SearchResponse>(`/search?${queryParams.toString()}`);
  },
};

// Dashboard API
export const dashboardApi = {
  getActionItems: () => api.get<{
    unreadMessages: Array<any>;
    tasksDue: Array<any>;
    mentions: Array<any>;
  }>('/dashboard/action-items'),

  getAgencyQueue: () => api.get<{
    items: Array<{
      id: string;
      title: string;
      project: { id: string; name: string };
      author: { id: string; name: string; avatarUrl?: string };
      discussionStatus: string;
      lastReplyPreview: string;
      waitingDays: number;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }>('/dashboard/agency-queue'),
};

// Apps API (uses shell API)
export const appsApi = {
  list: () => shellApi.get<Array<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    icon: string;
    color: string;
    isCore: boolean;
    sortOrder: number;
  }>>('/apps'),
  myApps: () => shellApi.get<Array<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    icon: string;
    color: string;
    isCore: boolean;
    sortOrder: number;
  }>>('/apps/my'),
};

// Applications API (Accounts service - for managing OAuth apps and their org assignments)
export interface AppOrganization {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  logo: string | null;
  memberCount: number;
  assignedAt: string;
  assignedBy: string | null;
}

export const applicationsApi = {
  /**
   * Get all organizations assigned to the solid-hub application.
   * This is used by agency admins to see all client organizations.
   */
  getHubOrganizations: async (): Promise<AppOrganization[]> => {
    const result = await shellApi.get<{
      organizations: AppOrganization[];
      total: number;
    }>(`/applications/${SOLID_HUB_APP_ID}/organizations`);
    return result.organizations;
  },
};

// Attachments API
export interface Attachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storageKey: string;
  createdAt: string;
  uploadedBy: { id: string; name: string; avatarUrl?: string };
  url?: string; // Presigned download URL (populated by backend for task comments)
  thumbnailSmUrl?: string; // Presigned URL for small thumbnail (Lambda-generated)
  thumbnailMdUrl?: string; // Presigned URL for medium thumbnail (Lambda-generated)
}

// Project Files types
export interface ProjectFile {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  mimeCategory?: string;
  storageKey: string;
  createdAt: string;
  folderId?: string;
  source: 'direct' | 'discussion' | 'task';
  sourceId?: string;
  sourceName?: string;
  uploadedBy?: { id: string; name: string; avatarUrl?: string };
}

export interface ProjectFilesQuery {
  source?: 'all' | 'direct' | 'discussions' | 'tasks';
  folderId?: string; // 'root' for root-level files, or folder ID
  limit?: number;
  offset?: number;
  sortBy?: 'date' | 'name' | 'size';
  sortOrder?: 'asc' | 'desc';
}

// File Folders
export interface FileFolder {
  id: string;
  name: string;
  projectId: string;
  parentId: string | null;
  isSystem: boolean;
  createdAt: string;
  createdBy?: { id: string; name: string };
  _count?: { attachments: number; children: number; totalFiles?: number };
}

export const attachmentsApi = {
  getUploadUrl: (data: {
    organizationId: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    entityType: 'taskComment' | 'task' | 'message' | 'directMessage' | 'project' | 'formResponse' | 'messageDraft';
    entityId: string;
    folderId?: string;
  }) => api.post<{ uploadUrl: string; attachmentId: string; storageKey: string }>('/attachments/upload-url', data),

  getDownloadUrl: (attachmentId: string) =>
    api.get<{ downloadUrl: string; thumbnailSmUrl?: string; thumbnailMdUrl?: string }>(`/attachments/${attachmentId}/download-url`),

  /** Server-streamed download URL (bypasses CORS/opaque-cache issues) */
  getServerDownloadUrl: (attachmentId: string) =>
    `${API_BASE}/attachments/${attachmentId}/download`,

  listForEntity: (entityType: 'taskComment' | 'task' | 'message' | 'directMessage' | 'formResponse', entityId: string) =>
    api.get<Attachment[]>(`/attachments/entity/${entityType}/${entityId}`),

  listProjectFiles: (projectId: string, query?: ProjectFilesQuery) => {
    const params = new URLSearchParams();
    if (query?.source) params.append('source', query.source);
    if (query?.folderId) params.append('folderId', query.folderId);
    if (query?.limit) params.append('limit', String(query.limit));
    if (query?.offset) params.append('offset', String(query.offset));
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
    const queryString = params.toString();
    return api.get<{ files: ProjectFile[]; total: number }>(
      `/attachments/project/${projectId}${queryString ? `?${queryString}` : ''}`
    );
  },

  delete: (attachmentId: string) => api.delete<{ success: boolean }>(`/attachments/${attachmentId}`),

  moveFile: (attachmentId: string, folderId: string | null) =>
    api.patch<ProjectFile>(`/attachments/${attachmentId}/move`, { folderId }),

  // Folder operations
  listFolders: (projectId: string) =>
    api.get<FileFolder[]>(`/attachments/project/${projectId}/folders`),

  createFolder: (projectId: string, name: string, parentId?: string) =>
    api.post<FileFolder>(`/attachments/project/${projectId}/folders`, { name, parentId }),

  renameFolder: (projectId: string, folderId: string, name: string) =>
    api.patch<FileFolder>(`/attachments/project/${projectId}/folders/${folderId}`, { name }),

  deleteFolder: (projectId: string, folderId: string) =>
    api.delete<{ success: boolean }>(`/attachments/project/${projectId}/folders/${folderId}`),

  moveFolder: (projectId: string, folderId: string, parentId: string | null) =>
    api.patch<FileFolder>(`/attachments/project/${projectId}/folders/${folderId}/move`, { parentId }),

  // Trash operations
  trashFile: (attachmentId: string) =>
    api.patch<{ success: boolean }>(`/attachments/${attachmentId}/trash`, {}),

  trashFolder: (folderId: string) =>
    api.patch<{ success: boolean }>(`/attachments/folders/${folderId}/trash`, {}),

  restoreFile: (attachmentId: string) =>
    api.patch<{ success: boolean }>(`/attachments/${attachmentId}/restore`, {}),

  restoreFolder: (folderId: string) =>
    api.patch<{ success: boolean }>(`/attachments/folders/${folderId}/restore`, {}),

  listTrash: (projectId: string) =>
    api.get<{
      files: (ProjectFile & { trashedAt: string; trashedByName?: string })[];
      folders: (FileFolder & { trashedAt: string; trashedByName?: string })[];
    }>(`/attachments/project/${projectId}/trash`),

  emptyTrash: (projectId: string) =>
    api.delete<{ success: boolean }>(`/attachments/project/${projectId}/trash`),

  bulkTrash: (projectId: string, fileIds: string[], folderIds: string[]) =>
    api.patch<{ success: boolean }>(`/attachments/project/${projectId}/bulk-trash`, { fileIds, folderIds }),

  bulkDownload: async (projectId: string, fileIds: string[]): Promise<Blob> => {
    const token = useAuthStore.getState().accessToken;
    const response = await fetch(`${API_BASE}/attachments/project/${projectId}/bulk-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ fileIds }),
    });
    if (!response.ok) {
      throw new Error('Download failed');
    }
    return response.blob();
  },

  // Helper: Upload file directly (combines getUploadUrl + PUT to S3)
  async uploadFile(
    file: File,
    organizationId: string,
    entityType: 'taskComment' | 'task' | 'message' | 'directMessage' | 'project' | 'formResponse' | 'messageDraft',
    entityId: string,
    onProgress?: (percent: number) => void,
    folderId?: string
  ): Promise<{ attachmentId: string }> {
    // Get presigned URL
    const { uploadUrl, attachmentId } = await this.getUploadUrl({
      organizationId,
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      entityType,
      entityId,
      folderId,
    });

    // Upload directly to S3
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    return { attachmentId };
  },
};

// Users API (uses shell API)
export const usersApi = {
  getProfile: () => shellApi.get<{
    id: string;
    email: string;
    name: string;
    avatarUrl?: string;
    createdAt: string;
    organizations: Array<{
      organization: { id: string; name: string; slug: string };
    }>;
  }>('/users/me'),

  updateProfile: (data: { name?: string }) =>
    shellApi.post<{ id: string; name: string }>('/users/me', data),

  uploadAvatar: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = getAccessToken();
    const res = await fetch(`${API_BASE}/profile/avatar`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      const errorBody = await res.text().catch(() => '');
      throw new Error(`Upload failed: ${errorBody || res.statusText}`);
    }
    const data = await res.json();
    return data.url;
  },

  deleteAvatar: async (): Promise<void> => {
    await api.delete('/profile/avatar');
  },
};

// Reactions API
export type ReactionEntityType = 'message' | 'directMessage' | 'task' | 'taskComment';

export interface ReactionSummary {
  emoji: string;
  count: number;
  users: Array<{ id: string; name: string }>;
  hasReacted: boolean;
}

export const reactionsApi = {
  toggle: (entityType: ReactionEntityType, entityId: string, emoji: string) =>
    api.post<{ added: boolean; reactions: ReactionSummary[] }>(`/reactions/${entityType}/${entityId}`, { emoji }),

  get: (entityType: ReactionEntityType, entityId: string) =>
    api.get<ReactionSummary[]>(`/reactions/${entityType}/${entityId}`),

  getBatch: (entityType: ReactionEntityType, entityIds: string[]) =>
    api.get<Record<string, ReactionSummary[]>>(`/reactions/${entityType}?ids=${entityIds.join(',')}`),
};

// Activity API
export type ActivityType =
  | 'MENTION'
  | 'REPLY'
  | 'TASK_ASSIGNED'
  | 'TASK_COMPLETED'
  | 'DISCUSSION_CREATED'
  | 'FILE_SHARED'
  | 'PROJECT_ADDED'
  | 'FORM_ASSIGNED'
  | 'FORM_SUBMITTED';

export type EntityType = 'MESSAGE' | 'TASK' | 'FILE' | 'PROJECT' | 'FORM';

export interface Activity {
  id: string;
  userId: string;
  type: ActivityType;
  title: string;
  description?: string;
  actorId?: string;
  actor?: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  projectId?: string;
  project?: {
    id: string;
    name: string;
    organization?: { id: string; name: string };
  };
  entityType?: EntityType;
  entityId?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

// Grouped activity includes count and multiple actors
export interface GroupedActivity extends Activity {
  count: number;
  actorIds: string[];
  actors: { id: string; name: string; avatarUrl?: string }[];
  hasUnread: boolean;
}

export interface ActivityListResponse {
  items: Activity[];
  unreadCount: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface GroupedActivityListResponse {
  items: GroupedActivity[];
  unreadCount: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const activityApi = {
  list: (params?: { page?: number; limit?: number; type?: ActivityType; unreadOnly?: boolean; grouped?: boolean; projectId?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.type) query.append('type', params.type);
    if (params?.unreadOnly) query.append('unreadOnly', 'true');
    if (params?.grouped) query.append('grouped', 'true');
    if (params?.projectId) query.append('projectId', params.projectId);
    return api.get<GroupedActivityListResponse>(`/activity?${query.toString()}`);
  },

  getUnreadCount: () => api.get<{ count: number }>('/activity/unread-count'),

  markAsRead: (id: string) => api.patch<{ success: boolean }>(`/activity/${id}/read`, {}),

  markAllAsRead: () => api.patch<{ success: boolean }>('/activity/read-all', {}),

  markGroupAsRead: (params: { entityType?: EntityType | null; entityId?: string | null; activityType: ActivityType }) =>
    api.post<{ success: boolean }>('/activity/group/read', params),
};

// Settings API
export interface GoogleDriveStatus {
  configured: boolean;
  serviceAccountEmail?: string;
  projectId?: string;
}

export const settingsApi = {
  getGoogleDriveStatus: () => api.get<GoogleDriveStatus>('/settings/google-drive/status'),

  uploadGoogleDriveCredentials: (credentialsJson: string) =>
    api.post<{ success: boolean; serviceAccountEmail?: string; projectId?: string; error?: string }>(
      '/settings/google-drive/credentials',
      { credentialsJson }
    ),

  removeGoogleDriveCredentials: () =>
    api.delete<{ success: boolean }>('/settings/google-drive/credentials'),
};

// Plugins API
export interface PluginMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  iconUrl?: string;
  requiresOAuth: boolean;
  supportedEvents: string[];
}

export interface PluginInstallation {
  id: string;
  pluginId: string;
  organizationId: string;
  projectId?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  credentials?: {
    teamId?: string;
    teamName?: string;
    scope?: string;
    hasAccessToken: boolean;
    hasAppConfig?: boolean;
  };
  eventMappings?: PluginEventMapping[];
}

export interface PluginEventMapping {
  id: string;
  installationId: string;
  eventType: string;
  enabled: boolean;
  channelConfig: Record<string, unknown>;
  filterRules?: Record<string, unknown>;
  isGlobal: boolean;
  scope: string;
  scopeId: string;
}

export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

export const pluginsApi = {
  // List all available plugins
  listPlugins: () =>
    api.get<{ plugins: PluginMetadata[]; supportedEvents: string[] }>('/plugins'),

  // Get plugin details
  getPlugin: (pluginId: string) => api.get<PluginMetadata>(`/plugins/${pluginId}`),

  // Install a plugin
  install: (pluginId: string, organizationId: string, projectId?: string) =>
    api.post<{ installation: PluginInstallation; requiresOAuth: boolean }>(
      `/plugins/${pluginId}/install`,
      { organizationId, projectId }
    ),

  // List installations for an organization
  listInstallations: (pluginId: string, organizationId: string) =>
    api.get<PluginInstallation[]>(
      `/plugins/${pluginId}/installations?organizationId=${organizationId}`
    ),

  // Get installation details
  getInstallation: (pluginId: string, installationId: string) =>
    api.get<PluginInstallation>(`/plugins/${pluginId}/installations/${installationId}`),

  // Update installation
  updateInstallation: (pluginId: string, installationId: string, data: { enabled?: boolean }) =>
    api.patch<PluginInstallation>(`/plugins/${pluginId}/installations/${installationId}`, data),

  // Uninstall
  uninstall: (pluginId: string, installationId: string) =>
    api.delete(`/plugins/${pluginId}/installations/${installationId}`),

  // Test connection
  testConnection: (pluginId: string, installationId: string) =>
    api.post<{ success: boolean; message?: string }>(
      `/plugins/${pluginId}/installations/${installationId}/test`
    ),

  // Save app configuration (OAuth credentials, etc.)
  saveAppConfig: (pluginId: string, installationId: string, config: Record<string, unknown>) =>
    api.post<{ success: boolean }>(
      `/plugins/${pluginId}/installations/${installationId}/config`,
      config
    ),

  // Get OAuth authorization URL
  getOAuthUrl: (pluginId: string, installationId: string) =>
    api.get<{ authorizationUrl: string; state: string }>(
      `/plugins/${pluginId}/oauth/authorize?installationId=${installationId}`
    ),

  // Event mappings
  upsertEventMapping: (
    pluginId: string,
    installationId: string,
    data: {
      eventType: string;
      enabled?: boolean;
      channelConfig: Record<string, unknown>;
      filterRules?: Record<string, unknown>;
      isGlobal?: boolean;
      scope?: string;
      scopeId?: string;
    }
  ) =>
    api.post<{ success: boolean }>(
      `/plugins/${pluginId}/installations/${installationId}/mappings`,
      data
    ),

  bulkUpsertEventMappings: (
    pluginId: string,
    installationId: string,
    mappings: Array<{
      eventType: string;
      enabled?: boolean;
      channelConfig: Record<string, unknown>;
      filterRules?: Record<string, unknown>;
      isGlobal?: boolean;
      scope?: string;
      scopeId?: string;
    }>
  ) =>
    api.post<{ success: boolean; count: number }>(
      `/plugins/${pluginId}/installations/${installationId}/mappings/bulk`,
      { mappings }
    ),

  getEventMappings: (
    pluginId: string,
    installationId: string,
    params?: { scope?: string; scopeId?: string }
  ) => {
    const query = new URLSearchParams();
    if (params?.scope) query.append('scope', params.scope);
    if (params?.scopeId) query.append('scopeId', params.scopeId);
    const qs = query.toString();
    return api.get<PluginEventMapping[]>(
      `/plugins/${pluginId}/installations/${installationId}/mappings${qs ? `?${qs}` : ''}`
    );
  },

  deleteEventMapping: (pluginId: string, installationId: string, mappingId: string) =>
    api.delete(`/plugins/${pluginId}/installations/${installationId}/mappings/${mappingId}`),

  deleteEventMappingsByScope: (
    pluginId: string,
    installationId: string,
    scope: string,
    scopeId: string,
  ) =>
    api.delete<{ success: boolean }>(
      `/plugins/${pluginId}/installations/${installationId}/mappings/scope/${scope}/${scopeId}`
    ),

  // Slack-specific
  slack: {
    listChannels: (installationId: string) =>
      api.get<{ channels: SlackChannel[] }>(
        `/plugins/slack/installations/${installationId}/channels`
      ),

    getProjectOverrides: (projectId: string) =>
      api.get<{
        projectId: string;
        projectName: string;
        installationId: string;
        mappings: PluginEventMapping[];
      }>(`/plugins/slack/project-overrides/${projectId}`),

    setProjectOverride: (projectId: string, channelId: string, channelName: string, eventTypes?: string[]) =>
      api.post<{ success: boolean; count: number }>(
        `/plugins/slack/project-overrides/${projectId}`,
        { channelId, channelName, ...(eventTypes?.length ? { eventTypes } : {}) }
      ),

    deleteProjectOverride: (projectId: string) =>
      api.delete<{ success: boolean }>(`/plugins/slack/project-overrides/${projectId}`),
  },

  // Google Drive-specific
  googleDrive: {
    getStatus: (installationId: string) =>
      api.get<{ configured: boolean; serviceAccountEmail?: string; projectId?: string }>(
        `/plugins/google-drive/installations/${installationId}/status`
      ),

    uploadCredentials: (installationId: string, credentialsJson: string) =>
      api.post<{ success: boolean; serviceAccountEmail?: string; projectId?: string; error?: string }>(
        `/plugins/google-drive/installations/${installationId}/credentials`,
        { credentialsJson }
      ),

    removeCredentials: (installationId: string) =>
      api.delete<{ success: boolean }>(
        `/plugins/google-drive/installations/${installationId}/credentials`
      ),

    listFolders: (installationId: string, folderId?: string) =>
      api.get<{
        folders: Array<{ id: string; name: string; mimeType: string }>;
        currentFolder?: { id: string; name: string };
        breadcrumb?: Array<{ id: string; name: string }>;
      }>(
        `/plugins/google-drive/installations/${installationId}/folders${folderId ? `?folderId=${folderId}` : ''}`
      ),
  },
};

// ClickUp API
export interface ClickupConfig {
  tokenConfigured: boolean;
  defaultListId: string | null;
  projectOverrides: Record<string, string>;
  projects: Array<{ id: string; name: string }>;
}

export const clickupApi = {
  getConfig: (organizationId: string) =>
    api.get<ClickupConfig>(`/clickup/config?organizationId=${encodeURIComponent(organizationId)}`),

  saveConfig: (organizationId: string, defaultListId: string) =>
    api.post<{ success: boolean }>('/clickup/config', { organizationId, defaultListId }),

  saveProjectOverride: (projectId: string, listId: string | null) =>
    api.post<{ success: boolean }>(`/clickup/config/projects/${projectId}`, { listId }),
};

// Notification Preferences API
export interface NotificationPreferences {
  emailNewDiscussion: boolean;
  emailMention: boolean;
  emailTaskAssigned: boolean;
  emailTaskReminder: boolean;
  emailAnnouncement: boolean;
  emailFormAssigned: boolean;
}

export const notificationPreferencesApi = {
  get: () => api.get<NotificationPreferences>('/notifications/preferences'),
  update: (data: Partial<NotificationPreferences>) =>
    api.put<NotificationPreferences>('/notifications/preferences', data),
};

// Announcements API
export interface Announcement {
  id: string;
  title: string;
  content: string;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  projects: Array<{ id: string; name: string }>;
  status: 'active' | 'scheduled' | 'expired' | 'draft';
}

export interface CreateAnnouncementData {
  title: string;
  content: string;
  projectIds?: string[];
  allProjects?: boolean;
  startsAt?: string;
  endsAt?: string;
  notifyUsers?: boolean;
}

export const announcementsApi = {
  // Admin: List all announcements
  list: () => api.get<Announcement[]>('/announcements'),

  // Admin: Get single announcement
  get: (id: string) => api.get<Announcement>(`/announcements/${id}`),

  // Admin: Create announcement
  create: (data: CreateAnnouncementData) => api.post<Announcement>('/announcements', data),

  // Admin: Update announcement
  update: (id: string, data: Partial<CreateAnnouncementData>) => api.patch<Announcement>(`/announcements/${id}`, data),

  // Admin: Delete announcement
  delete: (id: string) => api.delete<{ success: boolean }>(`/announcements/${id}`),

  // Public: Get active announcements for a project
  getActiveForProject: (projectId: string) => api.get<Array<{
    id: string;
    title: string;
    content: string;
    startsAt: string;
    endsAt: string | null;
    createdAt: string;
    createdBy: { id: string; name: string; avatarUrl?: string };
  }>>(`/announcements/project/${projectId}`),

  // Get active announcements for all projects the current user has access to
  getMyAnnouncements: () => api.get<Array<{
    id: string;
    title: string;
    content: string;
    startsAt: string;
    endsAt: string | null;
    createdAt: string;
    createdBy: { id: string; name: string; avatarUrl?: string };
    projects: Array<{ id: string; name: string }>;
  }>>('/announcements/my'),
};

// Project Export/Import API
export interface ExportPreview {
  manifest: {
    version: string;
    exportedAt: string;
    exportedBy: { id: string; name: string; email: string };
    projectCount: number;
  };
  projects: Array<{
    id: string;
    name: string;
    memberCount: number;
    messageCount: number;
    taskCount: number;
  }>;
  users: Array<{ id: string; email: string; name: string | null }>;
  existingUsers: Array<{ id: string; email: string; name: string | null }>;
  unmappedUsers: Array<{ id: string; email: string; name: string | null }>;
}

export interface ImportResult {
  success: boolean;
  importedProjects: Array<{ originalId: string; newId: string; name: string }>;
  errors: string[];
  warnings: string[];
}

export const projectExportApi = {
  // Export projects as ZIP file (triggers download)
  async exportProjects(projectIds: string[]): Promise<Blob> {
    const accessToken = getAccessToken();

    const response = await fetch(`${API_BASE}/export/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({ projectIds }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(error.message || 'Export failed');
    }

    return response.blob();
  },

  // Preview import file
  async previewImport(file: File): Promise<ExportPreview> {
    const accessToken = getAccessToken();

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/export/import/preview`, {
      method: 'POST',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Preview failed' }));
      throw new Error(error.message || 'Preview failed');
    }

    return response.json();
  },

  // Execute import
  async importProjects(
    file: File,
    targetOrganizationId: string,
    userMapping: Record<string, string>,
  ): Promise<ImportResult> {
    const accessToken = getAccessToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('targetOrganizationId', targetOrganizationId);
    formData.append('userMapping', JSON.stringify(userMapping));

    const response = await fetch(`${API_BASE}/export/import`, {
      method: 'POST',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Import failed' }));
      throw new Error(error.message || 'Import failed');
    }

    return response.json();
  },
};

// Calendar Task (for displaying tasks in calendar)
export interface CalendarTask {
  id: string;
  title: string;
  description?: string;
  dueDate: string;
  status: string;
  priority: string;
  assignee?: { id: string; name: string; avatarUrl?: string; accountStatus?: string };
  assigneeId?: string;
  createdById?: string;
  createdBy?: { id: string; name: string; avatarUrl?: string };
  list: { id: string; name: string };
  _count?: { comments: number };
}

// Events API (Calendar)
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startDate: string;
  endDate?: string | null;
  isAllDay: boolean;
  color?: string;
  createdAt: string;
  createdBy: { id: string; name: string; avatarUrl?: string };
  project?: { id: string; name: string };
}

export const eventsApi = {
  // Get events for a project
  getByProject: (projectId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    return api.get<CalendarEvent[]>(`/events/project/${projectId}${query ? `?${query}` : ''}`);
  },

  // Get upcoming events for current user
  getUpcoming: (limit = 5) =>
    api.get<CalendarEvent[]>(`/events/upcoming?limit=${limit}`),

  // Get single event
  getOne: (id: string) => api.get<CalendarEvent>(`/events/${id}`),

  // Create event
  create: (projectId: string, data: {
    title: string;
    description?: string;
    startDate: string;
    endDate?: string;
    isAllDay?: boolean;
    color?: string;
  }) => api.post<CalendarEvent>(`/events/project/${projectId}`, data),

  // Update event
  update: (id: string, data: {
    title?: string;
    description?: string;
    startDate?: string;
    endDate?: string | null;
    isAllDay?: boolean;
    color?: string;
  }) => api.patch<CalendarEvent>(`/events/${id}`, data),

  // Duplicate event
  duplicateEvent: (id: string) => api.post<any>(`/events/${id}/duplicate`, {}),

  // Delete event
  delete: (id: string) => api.delete(`/events/${id}`),
};

// Basecamp Integration API
export type ImportMode = 'skip' | 'sync_new' | 'replace';

export interface BasecampAccountInfo {
  id: string;
  name: string;
  product: string;
}

export interface BasecampProjectPreview {
  basecampId: number;
  name: string;
  parsedOrgName: string;
  parsedProjectName: string;
  description: string;
  archived: boolean;
  isClientProject: boolean;
  itemCounts: {
    todolists: number;
    topics: number;
    documents: number;
    calendarEvents: number;
    attachments: number;
  };
  importStatus: 'new' | 'imported' | 'has_changes';
  hubProjectId?: string;
  lastImportedAt?: string;
}

export interface ImportJobStatus {
  id: string;
  accountName: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: Record<string, {
    resource: string;
    total: number;
    imported: number;
    skipped: number;
    errors: Array<{ basecampId: number; resourceType: string; message: string }>;
  }>;
  summary?: {
    totalImported: number;
    totalSkipped: number;
    totalErrors: number;
    duration: number;
  };
  error?: string;
  startedAt: string;
  completedAt?: string;
}

export const basecampApi = {
  // OAuth
  getAuthUrl: () =>
    api.get<{ url: string }>('/basecamp/auth/url'),

  exchangeCode: (code: string) =>
    api.post<{ success: boolean; accounts: BasecampAccountInfo[] }>('/basecamp/auth/callback', { code }),

  // Accounts
  getAccounts: () =>
    api.get<{ accounts: BasecampAccountInfo[] }>('/basecamp/accounts'),

  disconnectAccount: (accountId: string) =>
    api.delete<void>(`/basecamp/accounts/${accountId}`),

  // Projects (with import status)
  getProjects: (accountId: string) =>
    api.get<{ projects: BasecampProjectPreview[] }>(`/basecamp/accounts/${accountId}/projects`),

  // Member preview (domain-based org assignment)
  previewMembers: (data: { accountId: string; projectIds: number[]; pendingOrgNames?: string[] }) =>
    api.post<{
      assignments: Array<{
        email: string;
        name: string;
        orgId: string | null;
        orgName: string | null;
        projects: number[];
        isExisting?: boolean;
      }>;
      organizations: Array<{ id: string; name: string }>;
      pendingOrganizations: Array<{ name: string }>;
    }>('/basecamp/import/preview-members', data),

  // Selective import
  startSelectiveImport: (data: {
    accountId: string;
    organizationId: string;
    projects: Array<{ basecampId: number; importMode: ImportMode }>;
    orgAssignments?: Array<{ email: string; orgId: string }>;
    projectOrgOverrides?: Array<{ basecampId: number; orgName: string }>;
    options?: { skipArchived?: boolean; skipTrashed?: boolean; includeAttachments?: boolean };
  }) => api.post<{ jobId: string; message: string }>('/basecamp/import/selective', data),

  // Jobs
  getJobs: () =>
    api.get<{ jobs: Array<{ id: string; accountName: string; status: string; startedAt: string; completedAt?: string }> }>('/basecamp/import/jobs'),

  getJobStatus: (jobId: string) =>
    api.get<ImportJobStatus>(`/basecamp/import/jobs/${jobId}`),

  cancelJob: (jobId: string) =>
    api.post<{ message: string }>(`/basecamp/import/jobs/${jobId}/cancel`),

  getImportStats: () =>
    api.get<{ stats: Record<string, number> }>('/basecamp/import/stats'),
};

export const giphyApi = {
  trending: (offset = 0, limit = 20) =>
    api.get<any>(`/giphy/trending?offset=${offset}&limit=${limit}`),
  search: (q: string, offset = 0, limit = 20) =>
    api.get<any>(`/giphy/search?q=${encodeURIComponent(q)}&offset=${offset}&limit=${limit}`),
};

// ============================================
// Forms — built by agency members, filled by assignees / project members
// ============================================

export type FormFieldType =
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'email'
  | 'url'
  | 'single_choice'
  | 'multiple_choice'
  | 'yes_no'
  | 'date'
  | 'section'
  | 'file';

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  helpText?: string;
  required?: boolean;
  options?: string[];
  maxFiles?: number;
}

export type FormStatus = 'draft' | 'published' | 'archived';
export type FormResponseStatus = 'pending' | 'draft' | 'submitted';
export type FormAnswers = Record<string, unknown>;

export interface FormUserRef {
  id: string;
  name: string | null;
  email: string;
  avatarUrl?: string | null;
  isStaff?: boolean;
}

export interface Form {
  id: string;
  organizationId: string;
  projectId: string | null;
  title: string;
  description: string | null;
  fields: FormField[];
  version: number;
  status: FormStatus;
  openSubmissions: boolean;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  project?: { id: string; name: string; organizationId?: string; archivedAt?: string | null } | null;
  createdBy?: FormUserRef;
}

export interface FormResponseSummary {
  id: string;
  status: FormResponseStatus;
  dueDate?: string | null;
  submittedAt?: string | null;
  updatedAt?: string;
}

export interface FormCounts {
  pending: number;
  draft: number;
  submitted: number;
}

/** Row in a list: a form plus what it means for me. */
export interface FormListItem extends Form {
  counts: FormCounts;
  myDraft: FormResponseSummary | null;
  myLastSubmitted: FormResponseSummary | null;
  mySubmittedCount: number;
}

export interface FormDetail extends Form {
  myResponses: FormResponseSummary[];
}

export interface FormResponseAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt?: string;
}

export interface FormResponse {
  id: string;
  formId: string;
  userId: string;
  status: FormResponseStatus;
  answers: FormAnswers;
  fieldsSnapshot?: FormField[] | null;
  formVersion?: number | null;
  assignedById?: string | null;
  dueDate?: string | null;
  note?: string | null;
  submittedAt?: string | null;
  reopenedAt?: string | null;
  reopenedById?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: FormUserRef;
  assignedBy?: FormUserRef | null;
  attachments?: FormResponseAttachment[];
  form?: Form;
  /** The fields this response is (or will be) filled against. */
  effectiveFields?: FormField[];
}

export interface MyFormsResult {
  assigned: Array<FormResponse & { form: Form }>;
  submitted: Array<FormResponse & { form: Form }>;
  open: Array<{
    id: string;
    title: string;
    description: string | null;
    projectId: string;
    project: { id: string; name: string };
    myDraft: FormResponseSummary | null;
    myLastSubmitted: FormResponseSummary | null;
    mySubmittedCount: number;
  }>;
}

export interface FormAnswerError {
  fieldId: string;
  message: string;
}

export const formsApi = {
  my: () => api.get<MyFormsResult>('/forms/my'),
  listForProject: (projectId: string) => api.get<FormListItem[]>(`/forms?projectId=${projectId}`),
  manage: (opts?: { scope?: 'global' | 'project' | 'all'; projectId?: string; includeArchived?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.scope) params.set('scope', opts.scope);
    if (opts?.projectId) params.set('projectId', opts.projectId);
    if (opts?.includeArchived) params.set('includeArchived', 'true');
    const qs = params.toString();
    return api.get<Array<Form & { counts: FormCounts }>>(`/forms/manage${qs ? `?${qs}` : ''}`);
  },
  assignableUsers: (projectId?: string | null, q?: string) => {
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    if (q) params.set('q', q);
    const qs = params.toString();
    return api.get<FormUserRef[]>(`/forms/assignable-users${qs ? `?${qs}` : ''}`);
  },
  create: (data: { projectId?: string | null; title: string; description?: string; fields?: FormField[]; openSubmissions?: boolean }) =>
    api.post<Form>('/forms', data),
  get: (formId: string) => api.get<FormDetail>(`/forms/${formId}`),
  update: (
    formId: string,
    data: { title?: string; description?: string; fields?: FormField[]; openSubmissions?: boolean; status?: FormStatus },
  ) => api.patch<Form>(`/forms/${formId}`, data),
  duplicate: (formId: string) => api.post<Form>(`/forms/${formId}/duplicate`),
  remove: (formId: string) => api.delete<{ success: boolean }>(`/forms/${formId}`),
  assign: (formId: string, data: { userIds: string[]; dueDate?: string; note?: string }) =>
    api.post<{ created: FormResponse[]; skipped: string[] }>(`/forms/${formId}/assign`, data),
  updateAssignment: (formId: string, responseId: string, data: { dueDate?: string | null; note?: string | null }) =>
    api.patch<FormResponse>(`/forms/${formId}/responses/${responseId}/assignment`, data),
  deleteResponse: (formId: string, responseId: string) =>
    api.delete<{ success: boolean }>(`/forms/${formId}/responses/${responseId}`),
  listResponses: (formId: string) => api.get<{ fields: FormField[]; responses: FormResponse[] }>(`/forms/${formId}/responses`),
  start: (formId: string) => api.post<FormResponse>(`/forms/${formId}/responses/start`),
  getResponse: (formId: string, responseId: string) => api.get<FormResponse>(`/forms/${formId}/responses/${responseId}`),
  saveAnswers: (formId: string, responseId: string, answers: FormAnswers) =>
    api.patch<FormResponse>(`/forms/${formId}/responses/${responseId}`, { answers }),
  submit: (formId: string, responseId: string, answers?: FormAnswers) =>
    api.post<FormResponse>(`/forms/${formId}/responses/${responseId}/submit`, answers ? { answers } : {}),
  reopen: (formId: string, responseId: string) => api.post<FormResponse>(`/forms/${formId}/responses/${responseId}/reopen`),
  exportCsvUrl: (formId: string) => `${API_BASE}/forms/${formId}/responses.csv`,
};

// ---------------------------------------------------------------------------
// Financials time — the hub as a front door. See docs/financials-time.md.
// ---------------------------------------------------------------------------

/**
 * A client's support hour bank.
 *
 * ⚠ THE BANK BELONGS TO THE CLIENT, NOT THE PROJECT. `clientName` and
 * `projectCount` are not decoration: several support projects can draw the
 * same pool down, so any surface printing `balance` must print them too.
 */
export interface HourBank {
  clientId: string;
  clientName: string;
  /** How many support projects share this pool. */
  projectCount: number;
  purchased: number;
  consumed: number;
  /** Hours worked past the bank. `balance` is never negative; this is the shortfall. */
  overage: number;
  balance: number;
  pctRemaining: number | null;
  status: 'good' | 'ok' | 'low' | 'exhausted' | 'overage' | null;
  burnPerWeek: number;
  runsOutOn: string | null;
  /** Why it runs out. "at this rate" is a lie when the hours simply expire. */
  runsOutReason: 'burn' | 'expiry' | null;
}

export interface FinancialsTimeStatus {
  configured: boolean;
  connected: boolean;
  hasPerson: boolean;
  reason?: string;
  detail?: string;
}

export interface FinancialsConnectionState {
  /** What the integration will actually USE — may be the environment. */
  active: {
    source: 'database' | 'environment';
    baseUrl: string;
    /** Whether those credentials actually reach financials — probed, not assumed. */
    reachable: boolean;
    /** Why not, when they don't. */
    reason?: string;
  } | null;
  /** What is stored in the database. The key itself is NEVER returned. */
  stored: {
    enabled: boolean;
    baseUrl: string | null;
    /** Last four characters, e.g. "…a91c". Enough to tell two keys apart. */
    apiKeyHint: string | null;
    updatedAt: string | null;
  } | null;
}

export const financialsConnectionApi = {
  get: () => api.get<FinancialsConnectionState>('/financials-time/connection'),
  save: (data: { baseUrl: string; apiKey?: string; enabled?: boolean }) =>
    api.post<FinancialsConnectionState>('/financials-time/connection', data),
  test: (data: { baseUrl: string; apiKey?: string }) =>
    api.post<{ ok: boolean; consumer?: string; scope?: string; reason?: string; detail?: string }>(
      '/financials-time/connection/test',
      data,
    ),
  remove: () => api.delete<FinancialsConnectionState>('/financials-time/connection'),
};

export const financialsTimeApi = {
  status: () => api.get<FinancialsTimeStatus>('/financials-time/status'),
  /**
   * Financials owns the link, and it is MANY financials projects (one per
   * support agreement) to ONE hub project — so the answer is a count, never a
   * single financials id for the hub to hold onto.
   */
  binding: (hubProjectId: string) =>
    api.get<{ hubProjectId: string; linked: boolean; linkedCount: number }>(
      `/financials-time/projects/${hubProjectId}/binding`,
    ),
  hours: (hubProjectId: string) =>
    api.get<{ linked: boolean; linkedCount: number; bank: HourBank | null }>(
      `/financials-time/projects/${hubProjectId}/hours`,
    ),
  /** Agency admins: financials reconciles the bank now, then answers like `hours`. */
  refreshHours: (hubProjectId: string) =>
    api.post<{ linked: boolean; linkedCount: number; bank: HourBank | null }>(
      `/financials-time/projects/${hubProjectId}/hours/refresh`,
      {},
    ),
  /** Client-safe: balance and expiry, never the burn pace and never money. */
  clientHours: (hubProjectId: string) =>
    api.get<{ linked: boolean; bank: ClientHours | null }>(
      `/financials-time/projects/${hubProjectId}/client-hours`,
    ),
  /** Emails the client's billing contact their secure hours page. */
  requestHoursLink: (hubProjectId: string) =>
    api.post<{ outcome: string; to: string }>(
      `/financials-time/projects/${hubProjectId}/request-hours-link`,
      {},
    ),
};

export interface ClientHours {
  clientName: string;
  purchased: number;
  used: number;
  overage: number;
  balance: number;
  pctRemaining: number | null;
  status: string | null;
  expiringSoon: Array<{ remaining: number; expiresAt: string; inDays: number }>;
  awaitingPayment: number;
}
